import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Gift, Star, Clock, CheckCircle, ArrowRight, 
  DollarSign, Sparkles, Trophy
} from "lucide-react";

const CASHBACK_AMOUNT = 2;

const MyRewardsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [completedReviews, setCompletedReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      // Fetch pending invitations
      const invResponse = await axios.get(`${API}/reviews/my-invitations`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setInvitations(invResponse.data);

      // Fetch completed reviews
      const reviewsResponse = await axios.get(`${API}/reviews/my-reviews`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCompletedReviews(reviewsResponse.data);
    } catch (error) {
      console.error("Error fetching rewards:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRemainingTime = (deadline) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diff = deadlineDate - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} días`;
    if (hours > 0) return `${hours} horas`;
    return "Expira pronto";
  };

  const totalEarned = completedReviews
    .filter(r => r.cashback_status === "sent")
    .reduce((sum, r) => sum + (r.cashback_amount || CASHBACK_AMOUNT), 0);

  const pendingCashback = completedReviews
    .filter(r => r.cashback_status === "pending")
    .reduce((sum, r) => sum + (r.cashback_amount || CASHBACK_AMOUNT), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />
      
      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
            <Gift size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Mis Recompensas</h1>
          <p className="text-[#64748B] text-sm mt-1">
            Gana cashback dejando reviews de tus compras
          </p>
        </div>

        {/* Description Card */}
        <div className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-2xl p-5 text-white mb-6">
          <div className="flex items-start gap-3">
            <Sparkles size={24} className="flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-lg mb-2">¿Cómo funciona?</h3>
              <ol className="space-y-2 text-sm text-white/90">
                <li className="flex items-start gap-2">
                  <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span>Compra un producto y completa el pago</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span>Recibirás una invitación para dejar tu review</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span>Deja tu review honesto con tu número de Yappy</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-white/20 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                  <span>¡Recibe <strong>${CASHBACK_AMOUNT}</strong> de cashback en tu Yappy!</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-[#E2E8F0] text-center">
            <DollarSign size={24} className="mx-auto text-[#10B981] mb-1" />
            <p className="text-2xl font-bold text-[#10B981]">${totalEarned}</p>
            <p className="text-xs text-[#64748B]">Ganado</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-[#E2E8F0] text-center">
            <Clock size={24} className="mx-auto text-[#F59E0B] mb-1" />
            <p className="text-2xl font-bold text-[#F59E0B]">${pendingCashback}</p>
            <p className="text-xs text-[#64748B]">Pendiente</p>
          </div>
        </div>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#0F172A] mb-3 flex items-center gap-2">
              <Star className="text-[#F59E0B]" size={20} />
              Reviews Pendientes
            </h2>
            <div className="space-y-3">
              {invitations.map((inv) => (
                <div 
                  key={inv.invitation_id}
                  className="bg-white rounded-xl border-2 border-[#F59E0B] p-4 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={inv.product_image}
                      alt={inv.product_title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-[#0F172A] truncate">{inv.product_title}</h4>
                      <p className="text-sm text-[#64748B]">Comprado por ${inv.product_price}</p>
                      <div className="flex items-center gap-1 text-xs text-[#F59E0B] mt-1">
                        <Clock size={12} />
                        <span>{getRemainingTime(inv.deadline)} para dejar review</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/review/${inv.product_id}`)}
                    className="w-full rounded-full h-11 bg-[#F59E0B] hover:bg-[#D97706]"
                    data-testid={`leave-review-${inv.product_id}`}
                  >
                    <Star size={18} className="mr-2" />
                    Dejar Review y Ganar ${CASHBACK_AMOUNT}
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Reviews */}
        {completedReviews.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-[#0F172A] mb-3 flex items-center gap-2">
              <Trophy className="text-[#10B981]" size={20} />
              Mis Reviews
            </h2>
            <div className="space-y-3">
              {completedReviews.map((review) => (
                <div 
                  key={review.review_id}
                  className="bg-white rounded-xl border border-[#E2E8F0] p-4"
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={review.product_image}
                      alt={review.product_title}
                      className="w-14 h-14 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-semibold text-[#0F172A] truncate">{review.product_title}</h4>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={14}
                              className={star <= review.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#E2E8F0]"}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-[#64748B] line-clamp-2 mt-1">{review.comment}</p>
                      
                      {/* Cashback Status */}
                      <div className={`mt-2 flex items-center gap-2 text-sm ${
                        review.cashback_status === "sent" 
                          ? "text-[#10B981]" 
                          : "text-[#F59E0B]"
                      }`}>
                        {review.cashback_status === "sent" ? (
                          <>
                            <CheckCircle size={16} />
                            <span>Cashback de ${review.cashback_amount} enviado</span>
                          </>
                        ) : (
                          <>
                            <Clock size={16} />
                            <span>Cashback de ${review.cashback_amount} pendiente</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {invitations.length === 0 && completedReviews.length === 0 && (
          <div className="text-center py-12">
            <Gift size={64} className="mx-auto text-[#CBD5E1] mb-4" />
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
              No tienes recompensas aún
            </h3>
            <p className="text-[#64748B] mb-4">
              Cuando compres un producto y confirmes el pago, recibirás una invitación para dejar tu review y ganar cashback.
            </p>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="rounded-full"
            >
              Explorar Productos
            </Button>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default MyRewardsPage;
